<template>
  <div class="top-menu">
    <div class="top-menu-actions">
      <span v-if="userInfo.cusName" class="company-name" :title="userInfo.cusName">
        <span>{{ userInfo.cusName }}</span>
        <el-tooltip v-if="showVipLevel && vipLevel" placement="bottom-start" effect="light">
          <template #content>
            <div class="vip-level-popover">
              <div class="vip-level-popover-title">客户等级体系</div>
              <div class="vip-level-popover-list">
                <div
                  v-for="(level, index) in vipLevelList"
                  :key="level.code"
                  :class="['vip-level-popover-item', { 'is-current': level.code === vipLevel.code }]"
                >
                  <span class="vip-level-popover-index">{{ index + 1 }}</span>
                  <img v-if="level.icon" :src="level.icon" :alt="level.name" />
                  <span>{{ level.name }} VIP</span>
                  <span v-if="level.code === vipLevel.code" class="vip-level-popover-current">当前等级</span>
                </div>
              </div>
            </div>
          </template>
          <span class="vip-level" :title="`${vipLevel.name} VIP`">
          <img
            v-if="vipLevel.icon"
            class="vip-level-icon"
            :src="vipLevel.icon"
            :alt="`${vipLevel.name} VIP`"
          />
          <span
            v-if="vipLevel.icon"
            class="vip-level-shine"
            :style="{ '--vip-icon': `url('${vipLevel.icon}')` }"
            aria-hidden="true"
          />
          <span v-else>{{ vipLevel.name }} VIP</span>
          </span>
        </el-tooltip>
      </span>
      <button
        type="button"
        class="notification-button"
        :aria-label="$t('消息通知')"
        @click="notificationVisible = true"
      >
        <el-badge
          :value="notificationUnreadCount"
          :hidden="!notificationUnreadCount"
          :max="99"
        >
          <el-icon><Bell /></el-icon>
        </el-badge>
      </button>
      <el-dropdown placement="bottom-end" @command="handleCommand">
        <span class="user-entry">
          <span class="user-avatar">{{ avatarText }}</span>
          <span class="accountName">{{
            userInfo?.userName || ""
          }}</span>
          <el-icon class="dropdown-icon">
            <CaretBottom />
          </el-icon>
        </span>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="appearance">
              {{ $t("界面设置") }}
            </el-dropdown-item>
            <el-dropdown-item command="myAddress">
              {{ $t("我的地址") }}
            </el-dropdown-item>
            <el-dropdown-item command="changePassword">
              {{ $t("修改密码") }}
            </el-dropdown-item>
            <el-dropdown-item command="loginout">{{
              $t("退出登录")
            }}</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
    <appearance-settings v-model="appearanceVisible" />
    <message-notification
      v-model="notificationVisible"
      @unread-change="notificationUnreadCount = $event"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { ElMessageBox } from "element-plus";
import { setCookie, getCookie } from "@/utils/cache";
import AppearanceSettings from "./AppearanceSettings.vue";
import MessageNotification from "./MessageNotification.vue";
import silverVipIcon from "@/assets/vip/白银.png";
import goldVipIcon from "@/assets/vip/黄金.png";
import platinumVipIcon from "@/assets/vip/铂金.png";

const emit = defineEmits(["change-password"]);
const { t, locale } = useI18n();
const store = useStore();
const router = useRouter();
const userInfo = computed(() => store.state.user.userInfo);
const vipLevels = {
  "018001": { name: "白银", icon: silverVipIcon },
  "018002": { name: "黄金", icon: goldVipIcon },
  "018003": { name: "铂金", icon: platinumVipIcon },
};
const vipLevelList = Object.entries(vipLevels).map(([code, level]) => ({ code, ...level }));
// 页面级开关：控制页眉是否展示客户等级及等级体系浮框。
const showVipLevel = false;
const vipLevel = computed(() => {
  const { vipLevelCode, vipLevelName } = userInfo.value || {};
  const level = vipLevels[vipLevelCode];
  if (!level && !vipLevelName) return null;
  return {
    code: vipLevelCode || "",
    name: vipLevelName || level?.name || "未知等级",
    icon: level?.icon,
  };
});
const appearanceVisible = ref(false);
const notificationVisible = ref(false);
const notificationUnreadCount = ref(0);


const avatarText = computed(
  () => userInfo.value?.userName.slice(0, 1).toUpperCase() || "U"
);

const handleCommand = (command) => {
  if (command === "appearance") {
    appearanceVisible.value = true;
  } else if (command === "myAddress") {
    router.push({ path: "/vip/system/myAddress" });
  } else if (command === "changePassword") {
    emit("change-password");
  } else if (command === "loginout") {
    ElMessageBox.confirm(t("确定退出登录？"), t("提示"), {
      confirmButtonText: t("确定"),
      cancelButtonText: t("取消"),
      type: "warning",
    }).then(() => {
      store.dispatch("LogOut").then(() => {
        location.reload();
      });
    });
  }
};

onMounted(() => {
  setLocale();
});

const setLocale = () => {
  if (!getCookie(import.meta.env.VITE_VUELANG_STR)) {
    setCookie(import.meta.env.VITE_VUELANG_STR, "zh");
  }
  const getLang = getCookie(import.meta.env.VITE_VUELANG_STR);
  locale.value = getLang;
};
</script>

<style lang="scss" scoped>
.top-menu {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
  height: 60px;
  padding: 0 28px;
  background: transparent;
}

.top-menu-actions {
  display: flex;
  align-items: center;
  height: 100%;
}

.company-name {
  display: inline-flex;
  align-items: center;
  margin-right: 18px;
  color: var(--app-text-color);
  font-size: var(--app-font-size);
  font-size: 16px;
  font-weight: 700;
}

.action-divider {
  width: 1px;
  height: 20px;
  margin: 0 18px;
  background: var(--app-border-color);
}

.user-entry {
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 5px 8px;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--app-surface-hover-color);
  }
}

.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin-right: 10px;
  color: #fff;
  font-size: var(--app-font-size);
  font-weight: 600;
  background: linear-gradient(135deg, #ff8a45, #f25b20);
  border-radius: 50%;
}

.accountName {
  max-width: 140px;
  overflow: hidden;
  color: var(--app-text-color);
  font-size: var(--app-font-size);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.vip-level {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  margin-left: 8px;
  color: var(--app-text-color);
  font-size: var(--app-font-size-12);
  white-space: nowrap;
  transition: transform 0.3s ease;
}

@media (hover: hover) {
  .vip-level:hover {
    transform: translateY(-1px) scale(1.04);
  }
}

.vip-level-shine {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    110deg,
    transparent 42%,
    rgb(255 255 255 / 12%) 46%,
    rgb(255 255 255 / 55%) 50%,
    rgb(255 255 255 / 12%) 54%,
    transparent 58%
  );
  background-size: 300% 100%;
  background-repeat: no-repeat;
  -webkit-mask: var(--vip-icon) center / contain no-repeat;
  mask: var(--vip-icon) center / contain no-repeat;
  animation: vip-shine 5s ease-in-out infinite;
}

@keyframes vip-shine {
  0%, 65% {
    background-position: 100% 0;
    opacity: 0;
  }
  70% {
    opacity: 1;
  }
  95%, 100% {
    background-position: 0% 0;
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .vip-level,
  .vip-level:hover {
    transition: none;
    transform: none;
  }

  .vip-level-shine {
    animation: none;
    display: none;
  }
}

.vip-level-icon {
  display: block;
  width: 72px;
  height: 34px;
  object-fit: contain;
}

.vip-level-popover {
  min-width: 190px;
  color: var(--app-text-color);
}

.vip-level-popover-title {
  padding-bottom: 8px;
  margin-bottom: 4px;
  color: var(--app-text-color);
  font-size: 13px;
  font-weight: 600;
  border-bottom: 1px solid var(--app-border-color);
}

.vip-level-popover-item {
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: 36px;
  padding: 3px 6px;
  color: var(--app-text-secondary-color);
  font-size: 12px;
  border-radius: 4px;
}

.vip-level-popover-item.is-current {
  color: #b45309;
  font-weight: 600;
  background: #fff7ed;
}

.vip-level-popover-index {
  width: 16px;
  color: var(--app-text-secondary-color);
  text-align: center;
}

.vip-level-popover-item img {
  width: 42px;
  height: 24px;
  object-fit: contain;
}

.vip-level-popover-current {
  margin-left: auto;
  color: #f26522;
  font-size: 11px;
}

.dropdown-icon {
  margin-left: 8px;
  color: var(--app-text-secondary-color);
  font-size: 12px;
}

.notification-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  margin-right: 10px;
  color: var(--app-text-color);
  background: transparent;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    color: #f26522;
    background: var(--app-surface-hover-color);
  }

  .el-icon {
    font-size: 22px;
  }

  :deep(.el-badge__content) {
    top: 2px;
    right: 6px;
    border: 0;
  }
}

@media (max-width: 720px) {
  .top-menu {
    padding: 0 16px;
  }

  .action-divider {
    margin: 0 10px;
  }

  .accountName {
    max-width: 90px;
  }

  .company-name {
    display: none;
  }
}
</style>
